import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';

import dayjs from 'dayjs';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import utilityApi from '~/apis/utility';

import { CloseIcon, EditLineIcon, InformationFillIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import useLanguage from '~/hooks/useLanguage';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetSensorTypes } from '~/thunks/master/masterThunk';
import { handleApiError } from '~/utils/helper';
import { IAlertByDevice, IEquipment, ISensorPort } from '~/utils/interface/equipment';

import EditSensorDialog from '../edit-sensor-dialog/EditSensorDialog';
import { DIALOG_MODE_EDIT } from '~/utils/constants/common';
import equipmentApi from '~/apis/equipment';
import ServiceDialog from '../service-dialog/ServiceDialog';
import DetailAlertDialog from '~/pages/admin/alert/component/detail-alert-dialog/DetailAlertDialog';

type DetailEquipmentDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  data?: IEquipment;
};

type StatusColor = { [key: string]: string };
const statusColors: StatusColor = {
  0: '#72777B',
  1: '#3D9149',
  2: '#FF9500',
  3: '#F32A4C',
};

const alarmStatusColors: StatusColor = {
  0: '#F32A4C',
  1: '#3D9149',
  2: '#3D9149',
  3: '#3D9149',
};

const DetailEquipmentDialog = (props: DetailEquipmentDialogProps) => {
  const { t } = useTranslation('admin/equipment');
  const language = useLanguage();
  const dispatch = useAppDispatch();
  const { sensorTypes } = useAppSelector((state) => state.master);
  const [agents, setAgents] = useState<any[]>([]);
  const [isOpenEditSensorDialog, setIsOpenEditSensorDialog] = useState<boolean>(false);
  const [editSensorDialogMode, setEditSensorDialogMode] = useState<string>(DIALOG_MODE_EDIT);
  const [sensor, setSensor] = useState<ISensorPort>();
  const [alertByDevices, setAlertByDevices] = useState<Array<IAlertByDevice>>([]);
  const [isOpenServiceDialog, setIsOpenServiceDialog] = useState<boolean>(false);
  const [isOpenDetailAlertDialog, setIsOpenDetailAlertDialog] = useState<boolean>(false);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);

  const sensorStatusMap: Record<
    string,
    (v: number | null, label: string) => { label: string; status: string; color: string | undefined }
  > = {
    fire_alarm: (v, label) => ({
      label: label,
      status:
        v == null
          ? t('sensorStatusLabel.null')
          : v === 1
          ? t('sensorStatusLabel.fireAlarm.1')
          : t('sensorStatusLabel.fireAlarm.0'),
      color: v == null ? undefined : v === 1 ? '#F32A4C' : '#3D9149',
    }),
    device_status: (v, label) => ({
      label: label,
      status:
        v == null
          ? t('sensorStatusLabel.null')
          : v === 1
          ? t('sensorStatusLabel.deviceStatus.1')
          : t('sensorStatusLabel.deviceStatus.0'),
      color: v == null ? undefined : v === 1 ? '#F32A4C' : '#3D9149',
    }),
    status: (v, label) => ({
      label: label,
      status:
        v == null
          ? t('sensorStatusLabel.null')
          : v === 1
          ? t('sensorStatusLabel.status.1')
          : t('sensorStatusLabel.status.0'),
      color: v == null ? undefined : v === 1 ? '#3D9149' : '#F32A4C',
    }),
    csq: (v, label) => ({
      label: label,
      status:
        v == null
          ? t('sensorStatusLabel.null')
          : v >= 15
          ? t('sensorStatusLabel.csq.1', { v })
          : t('sensorStatusLabel.csq.0'),
      color: v == null ? undefined : v >= 15 ? '#3D9149' : '#F32A4C',
    }),
    vin_state: (v, label) => ({
      label: label,
      status:
        v == null
          ? t('sensorStatusLabel.null')
          : v === 1
          ? t('sensorStatusLabel.vinState.1')
          : t('sensorStatusLabel.vinState.0'),
      color: v == null ? undefined : v === 1 ? '#3D9149' : '#F32A4C',
    }),
    vsr_state: (v, label) => ({
      label: label,
      status:
        v == null
          ? t('sensorStatusLabel.null')
          : v === 1
          ? t('sensorStatusLabel.vsrState.1')
          : t('sensorStatusLabel.vsrState.0'),
      color: v == null ? undefined : v === 1 ? '#3D9149' : '#F32A4C',
    }),
  };

  useEffect(() => {
    if (props.open) {
      (async () => {
        try {
          const response = await utilityApi.getListAgents();
          setAgents(response.data);
        } catch (error) {
          handleApiError(error);
        }
      })();

      dispatch(handleGetSensorTypes());

      (async () => {
        try {
          const response = await equipmentApi.getAlertByDevice(Number(props?.data?.device_id));
          setAlertByDevices(response.data);
        } catch (error) {
          handleApiError(error);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const handleRenderInfo = (label: string, key: any, value: number | null, color?: string) => {
    const handle = sensorStatusMap[key];
    const sensor = handle(value, label);

    if (!sensor) return null;
    return (
      <Box className='flex flex-row gap-[15px] mb-[10px]' style={{ color: color || '#000' }}>
        <Box className='font-bold'>{sensor.label}:</Box>
        <Box style={{ color: sensor.color }}>{sensor.status}</Box>
      </Box>
    );
  };

  const getStatus = (status: number | undefined) => {
    const color = statusColors[String(status) as keyof StatusColor];
    return (
      <Box className={`text-[${color}] border border-[${color}] rounded-[20px] px-[10px] py-[5.5px]`}>
        {t(`simStatuses.${status}`)}
      </Box>
    );
  };

  const getAgentName = (agent: string | undefined) => {
    const data = agents.find((item) => item.username === agent);
    if (data) return `${data.username} - ${data.name}`;
  };

  const getSensorTypeName = (id: number) => {
    const sensorType = sensorTypes.find((item) => Number(item.id) === id);
    if (!sensorType) return;
    return language.getValue('string_value', sensorType);
  };

  const handleSetIsOpenEditSensorDialog = (isOpen: boolean = false, needRefresh: boolean = false) => {
    setIsOpenEditSensorDialog(isOpen);
    if (needRefresh && props.setOpen) {
      props.setOpen(true, needRefresh);
    }
  };
  const handleSetOpenDetail = (open: boolean) => {
    setIsOpenDetailAlertDialog(open);
    if (!open) {
      setSelectedAlertId(null);
    }
  };
  return (
    <>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='lg'>
        <DialogTitle className='bg-[#005F73]'>
          {t('detailEquipmentDialog.title')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BaseRow className='gap-[100px]'>
            <Box className='w-1/2 relative'>
              <Button
                className='absolute top-[35%] right-0'
                variant='contained'
                color='secondary'
                onClick={() => setIsOpenServiceDialog(true)}
              >
                {t('detailEquipmentDialog.viewServiceStatus')}
              </Button>
              <Box className='flex flex-row gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailEquipmentDialog.deviceCode')}:</Box>
                <Box>{props.data?.device_code}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailEquipmentDialog.deviceName')}:</Box>
                <Box>{props.data?.device_name}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailEquipmentDialog.simNo')}:</Box>
                <Box>{props.data?.sim_no}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailEquipmentDialog.owner')}:</Box>
                <Box>{props.data?.owner}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailEquipmentDialog.address')}:</Box>
                <Box>{props.data?.address}</Box>
              </Box>
              {handleRenderInfo(t('detailEquipmentDialog.csq'), 'csq', props.data?.device_lastdata?.csq ?? null)}
              <Box className='flex flex-row gap-[15px] mb-[10px] font-bold'>
                {t('detailEquipmentDialog.electricState')}:
              </Box>
              {handleRenderInfo(
                t('detailEquipmentDialog.vinState'),
                'vin_state',
                props.data?.device_lastdata?.vin_state ?? null,
                '#005F73'
              )}
              {handleRenderInfo(
                t('detailEquipmentDialog.vsrState'),
                'vsr_state',
                props.data?.device_lastdata?.vsr_state ?? null,
                '#005F73'
              )}
              {handleRenderInfo(
                t('detailEquipmentDialog.status'),
                'status',
                props.data?.device_lastdata?.status ?? null
              )}
              <Box className='flex flex-row gap-[15px] mb-[10px] items-center'>
                <Box className='font-bold'>{t('detailEquipmentDialog.simStatus')}:</Box>
                {getStatus(props.data?.sim_status)}
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px] items-center'>
                <Box className='font-bold'>{t('detailEquipmentDialog.simExpireDate')}:</Box>
                <Box className={`text-[${statusColors[props.data?.sim_status as keyof StatusColor]}]`}>
                  {props.data?.sim_expire_date ? dayjs(props.data?.sim_expire_date).format('DD/M/YYYY') : null}
                </Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px] items-center'>
                <Box className='font-bold'>{t('detailEquipmentDialog.iotStatus')}:</Box>
                {getStatus(props.data?.iot_status)}
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px] items-center'>
                <Box className='font-bold'>{t('detailEquipmentDialog.expireDate')}:</Box>
                <Box className={`text-[${statusColors[props.data?.iot_status as keyof StatusColor]}]`}>
                  {props.data?.expire_date ? dayjs(props.data?.expire_date).format('DD/M/YYYY') : null}
                </Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailEquipmentDialog.agent')}:</Box>
                <Box>{getAgentName(props.data?.agent)}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailEquipmentDialog.distributor')}:</Box>
                <Box>{props.data?.distributor}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('detailEquipmentDialog.lastdataAt')}:</Box>
                <Box>{props.data?.lastdata_at ? dayjs(props.data?.lastdata_at).format('DD/MM/YYYY HH:mm:ss') : ''}</Box>
              </Box>
              <Box className='mb-[26px]'>
                <BaseRow className='mb-[5px] gap-[15px] w-full !justify-between !items-center'>
                  <Box className='font-bold text-[#005F73] text-[18px]'>{t('detailEquipmentDialog.sensorList')}</Box>
                  <Box className='font-bold rounded-full text-[#FFFFFF] bg-[#005F73] w-[30px] h-[30px] flex justify-center items-center'>
                    {props.data?.sensor_ports?.length || 0}
                  </Box>
                </BaseRow>
                {props.data?.sensor_ports?.length ? (
                  <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                    <Table>
                      <TableBody className='divide-y divide-[#005F73]'>
                        {props.data.sensor_ports.map((row, index) => {
                          return (
                            <TableRow key={index}>
                              <TableCell className='border-none'>
                                <Box>
                                  <Box className='font-bold mb-[5px]'>{row.sensor_code}</Box>
                                  <Box>{row.location}</Box>
                                </Box>
                              </TableCell>
                              <TableCell className='border-none text-right'>
                                <Box>
                                  <Box className='mb-[5px]'>
                                    {row.lastdata?.sensor_timestamp
                                      ? dayjs(row.lastdata?.sensor_timestamp).format('DD/MM/YYYY HH:mm')
                                      : null}
                                  </Box>
                                  <Box>
                                    <Box>{getSensorTypeName(row.sensor_type)}</Box>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell className='border-none w-[40px] hidden'>
                                <BaseRow>
                                  <IconButton
                                    onClick={() => {
                                      setSensor(row);
                                      setEditSensorDialogMode(DIALOG_MODE_EDIT);
                                      handleSetIsOpenEditSensorDialog(true);
                                    }}
                                  >
                                    <EditLineIcon className='[&>path]:fill-[#D9D9D9]' />
                                  </IconButton>
                                </BaseRow>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                ) : null}
              </Box>
            </Box>
            <Box className='w-1/2'>
              <Box className='mb-[36px]'>
                <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                  <Box className='font-bold text-[#005F73] text-[18px]'>{t('detailEquipmentDialog.alarmContacts')}</Box>
                </BaseRow>
                {props.data?.alarm_contacts?.length ? (
                  <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                    <Table>
                      <TableBody className='divide-y divide-[#005F73]'>
                        {props.data.alarm_contacts.map((row, index) => {
                          return (
                            <TableRow key={index}>
                              <TableCell className='border-none'>
                                <Box>
                                  <Box className='font-bold mb-[5px]'>{row.phone_number}</Box>
                                  <Box>{row.full_name}</Box>
                                </Box>
                              </TableCell>
                              <TableCell className='border-none text-right'>
                                <Box className='mb-[5px]'>{t(`notifyLevel.${row.manager_role}`)}</Box>
                                <Box className='mb-[5px]'>{t(`notifyTypes.${row.notify_type}`)}</Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                ) : null}
              </Box>
              <Box className='mb-[26px]'>
                <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                  <Box className='font-bold text-[#ED1002] text-[18px]'>
                    {t('detailEquipmentDialog.alarmByDevices')}
                  </Box>
                </BaseRow>
                {alertByDevices?.length ? (
                  <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                    <Table>
                      <TableBody className='divide-y divide-[#005F73]'>
                        {alertByDevices.map((row, index) => {
                          return (
                            <TableRow key={index}>
                              <TableCell className='border-none'>
                                <Box>
                                  <Box className='font-bold mb-[5px]'>{row.sensor_code}</Box>
                                  <Box>{row.location}</Box>
                                </Box>
                              </TableCell>
                              <TableCell className='border-none text-right pr-[2px]'>
                                <Box className='mb-[5px]'>
                                  {row.created_at ? dayjs(row.created_at).format('DD/MM/YYYY HH:mm') : null}
                                </Box>
                                <Box
                                  className={`border rounded-[2em] font-bold border-[${
                                    alarmStatusColors[row.status]
                                  }] text-[${alarmStatusColors[row.status]}] w-[120px] text-center ml-auto`}
                                >
                                  {t(`alarmStatuses.${row.status}`)}
                                </Box>
                              </TableCell>
                              <TableCell className='border-none w-[2px] p-2' align='center'>
                                <IconButton
                                  onClick={() => {
                                    setSelectedAlertId(row.id);
                                    setIsOpenDetailAlertDialog(true);
                                  }}
                                >
                                  <InformationFillIcon className='[&>path]:fill-[#D9D9D9]' />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </BaseRow>
        </DialogContent>
      </Dialog>
      <EditSensorDialog
        open={isOpenEditSensorDialog}
        mode={editSensorDialogMode}
        setOpen={handleSetIsOpenEditSensorDialog}
        equipment={props.data}
        data={sensor}
      />
      <ServiceDialog
        open={isOpenServiceDialog}
        setOpen={(isOpen) => setIsOpenServiceDialog(isOpen)}
        data={props.data}
      />
      <DetailAlertDialog open={isOpenDetailAlertDialog} data={{ id: selectedAlertId }} setOpen={handleSetOpenDetail} />
    </>
  );
};

export default memo(DetailEquipmentDialog);
